import { useEffect, useRef, useState } from 'react';

/**
 * Protecting work in progress from live updates.
 *
 * Live refetching is right almost everywhere: a coordinator watching a list wants it to
 * change under them as colleagues work. There is exactly one place it is wrong — a field
 * someone is currently typing into.
 *
 * Without this, the sequence is: Julie starts correcting a customer's address, Vanessa saves
 * an unrelated change to the same customer, the invalidation arrives, the form re-reads from
 * the server, and Julie's half-typed address disappears. She would have no idea why.
 *
 * So an open editor **registers itself as dirty**. While anything is dirty, an incoming
 * change to that record is held rather than applied, and the editor is told a newer version
 * exists. Nothing is lost and nothing is silently overwritten — the person doing the typing
 * decides.
 */

type RecordKey = string; // `${entity}:${id}`

const dirty = new Set<RecordKey>();
const pending = new Map<RecordKey, number>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

/** True when an editor somewhere is holding unsaved changes to this record. */
export function isDirty(entity: string, id: string): boolean {
  return dirty.has(`${entity}:${id}`);
}

/**
 * Record that a newer version arrived while an editor was open.
 * Called by the realtime layer instead of invalidating.
 */
export function deferUpdate(entity: string, id: string): void {
  const key = `${entity}:${id}`;
  pending.set(key, (pending.get(key) ?? 0) + 1);
  notify();
}

/**
 * Marks a record as being edited for as long as the component is mounted and `active`.
 *
 * Returns whether a newer version is waiting, and a way to accept it — so the UI can offer
 * "the office changed this, load their version" rather than deciding for the user.
 */
export function useEditGuard(entity: string, id: string | undefined, active: boolean) {
  const key = id ? `${entity}:${id}` : null;
  const [supersededBy, setSupersededBy] = useState(0);
  const keyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!key) return;
    keyRef.current = key;

    if (active) dirty.add(key);
    else dirty.delete(key);

    const listener = () => setSupersededBy(pending.get(key) ?? 0);
    listeners.add(listener);
    listener();

    return () => {
      listeners.delete(listener);
      // Releasing the lock must also clear the backlog, or the next editor of this record
      // opens to a stale "someone changed this" warning about a change already applied.
      dirty.delete(key);
      pending.delete(key);
    };
  }, [key, active]);

  return {
    /** How many newer versions arrived while this editor has been open. */
    supersededBy,
    /** Accept the newer version: clears the flag so the caller can refetch. */
    accept: () => {
      if (keyRef.current) pending.delete(keyRef.current);
      setSupersededBy(0);
    },
  };
}
